import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { observer, inject } from 'mobx-react';
import { injectIntl } from 'react-intl';
import { css } from '@emotion/core';
import { Form, Switch, Table, Popover } from 'antd';
import { SettingsFormHeader } from '../../common/SettingsFormHeader';
import CustomInput from '../../common/Input';
import { ContentRow } from './styled';
import { commaFormat } from '../../../utils/numberCommaFormatter';
import { themeColors } from '../../../constants/themes';
import Button from '../../common/Button/Button';
import IconSvg from '../../common/IconSvg';
import Spinner from '../../../components/common/spinner';
import PopUpVideoLink from '../../common/PopUpVideoLink/PopUpVideoLink';
import { text2Medium } from '../../../constants/classes';
import { theme } from '../../../constants';
import { getSignByProfitType } from '../../../utils/parsers';
// import { FormItem } from '../Subscription/styled';

const minLimit = 25;

@inject('settingsStore')
@inject('sessionStore')
@injectIntl
@Form.create()
@observer
class Balance extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      seeAllInvoices: false,
    };
  }

  componentDidMount() {
    this.props.updateBreadcrumbs(
      {
        name: this.props.intl.formatMessage({ id: 'settingsModal.balance' }),
      },
      true,
    );
    const {
      settingsStore: {
        balance: { fetchBalanceData },
      },
    } = this.props;

    fetchBalanceData();
  }

  getInvoice = (id, name) => {
    const {
      settingsStore: {
        balance: { getInvoiceFile },
      },
    } = this.props;
    getInvoiceFile(id, name);
  };

  setLoading = val => {
    this.setState({ loading: val });
  };

  loadMore = () => {
    const {
      settingsStore: {
        balance: { fetchNextTransactionsPage },
      },
    } = this.props;
    this.setLoading(true);
    fetchNextTransactionsPage(true).then(() => {
      this.setLoading(false);
    });
  };

  handleInput = (id, currencyId) => e => {
    const {
      autoReloadClone: {
        [currencyId]: { setAmount, setLimit },
      },
      updateApplyBtn,
    } = this.props;
    const givenValue = e.target ? e.target.value : 0;
    if (givenValue.indexOf('-') !== -1) {
      return;
    }
    switch (id) {
      case 'balanceTriggerLimit': {
        setLimit(parseFloat(givenValue));
        break;
      }
      case 'balanceLoadSum': {
        const parsedValue = parseFloat(givenValue);
        setAmount(parsedValue);
        if (!parsedValue || parsedValue < minLimit) {
          updateApplyBtn(true);
          return;
        }
        break;
      }
      default: {
        break;
      }
    }
    updateApplyBtn();
  };

  columns = [
    {
      title: 'Date',
      dataIndex: 'created',
      key: 'created',
      render: text => {
        return text && moment(text).format('YYYY-MM-DD');
      },
    },
    {
      title: 'Reload Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (text, { currency }) => {
        return `${getSignByProfitType(2, currency)} +${commaFormat(text)}`;
      },
    },
    {
      title: 'TripleMars Transaction',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'PayPal Transaction',
      dataIndex: 'external_id',
      key: 'external_id',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text, { extra }) => {
        return text === 'FAILED' ? (
          <Popover
            title=""
            content={
              <span
                css={css`
                  color: ${themeColors.pink};
                `}
              >
                {extra?.error_msg ?? 'No info'}
              </span>
            }
            trigger="hover"
            placement="left"
          >
            <IconSvg type="x-pink" size={20} style={{ cursor: 'pointer' }} />
          </Popover>
        ) : (
          <IconSvg type="v-green" size={20} />
        );
      },
    },
  ];

  columns_invoices = [
    {
      title: 'Date',
      dataIndex: 'createDate',
      key: 'createDate',
      render: text => {
        return text && moment(text).format('YYYY-MM-DD');
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Invoice Id',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Invoice',
      dataIndex: 'invoiceUrl',
      key: 'invoiceUrl',
      render: (text, { id, name }) => (
        <span onClick={() => this.getInvoice(id, name)} style={{ cursor: 'pointer' }}>
          <IconSvg type="pdf-file" />
        </span>
      ),
    },
  ];

  calculateCommission(value) {
    if (value < 100) {
      return 0.04 * value;
    } else if (value < 500) {
      return 0.03 * value;
    }
    return 0.025 * value;
  }

  render() {
    const {
      updateApplyBtn,
      intl,
      settingsStore: {
        balance: { deposits, invoices, paypalConnected, depositsPagination },
        userProfile: { defaultCurrencyId },
      },
      sessionStore: { isBifmUser },
      autoReloadClone,
    } = this.props;
    // filter deposits in regards to userProfile selected currency
    const localizedDeposits = deposits.filter(({ currency }) => currency === defaultCurrencyId);

    const { loading, seeAllInvoices } = this.state;
    const {
      [defaultCurrencyId]: { is_active, setActive, amount, limit },
    } = autoReloadClone;

    const hideForGBPandCAD = defaultCurrencyId === 'GBP' || defaultCurrencyId === 'CAD';

    return (
      <React.Fragment>
        {!paypalConnected ? (
          <div style={{ color: themeColors.pink }}>
            {intl.formatMessage({ id: 'balance.noPreviousTransaction' })}
          </div>
        ) : null}
        <Form layout="vertical">
          <SettingsFormHeader id="finance.balanceFormHeader" defaultMessage="BALANCE" />
          {!hideForGBPandCAD && (
            <>
              <ContentRow justifyContent="flex-start">
                <span>{intl.formatMessage({ id: 'finance.autoReloadBalance' })}</span>&nbsp;
                <Switch
                  disabled={!paypalConnected}
                  onChange={e => {
                    setActive(e);
                    updateApplyBtn();
                  }}
                  defaultChecked={is_active}
                  css={css`
                    border: 1px solid transparent !important;
                  `}
                />
              </ContentRow>
              <ContentRow justifyContent="flex-start" noMargin>
                <span>{intl.formatMessage({ id: 'finance.inputRowChunk1' })}</span>&nbsp;
                <CustomInput
                  disabled={!is_active || !paypalConnected}
                  type="text"
                  width="50px"
                  value={amount || ''}
                  onChange={this.handleInput('balanceLoadSum', defaultCurrencyId)}
                  prefix={getSignByProfitType(2, defaultCurrencyId)}
                  css={css`
                    display: flex;
                    margin-left: 15px;
                    & > input {
                      padding-left: 11px !important;
                    }
                    & > span.ant-input-prefix {
                      left: -13px;
                    }
                  `}
                />
                &nbsp;
                <span>{intl.formatMessage({ id: 'finance.inputRowChunk2' })}</span>&nbsp;
                <CustomInput
                  disabled={!is_active || !paypalConnected}
                  type="text"
                  width="50px"
                  value={limit || ''}
                  onChange={this.handleInput('balanceTriggerLimit', defaultCurrencyId)}
                  prefix={getSignByProfitType(2, defaultCurrencyId)}
                  css={css`
                    display: flex;
                    margin-left: 15px;
                    & > input {
                      padding-left: 11px !important;
                    }
                    & > span.ant-input-prefix {
                      left: -13px;
                    }
                  `}
                />
              </ContentRow>
            </>
          )}
          {hideForGBPandCAD && (
            <span>
              Autoreload is currently not available for{' '}
              {defaultCurrencyId === 'GBP' ? 'UK' : 'Canada'}.
            </span>
          )}
          {!hideForGBPandCAD && amount ? (
            <div>*Total Price: ${amount + this.calculateCommission(amount)}</div>
          ) : (
            <div />
          )}
          {!hideForGBPandCAD && (
            <div
              css={css`
                display: flex;
                justify-content: space-evenly;
                width: 100%;
                margin-top: 10px;
              `}
            >
              <PopUpVideoLink
                message="How To Auto Balance Reload:"
                videoUrl="https://vimeo.com/475865570/10c54b2b6c"
                extraCSS={`
                margin-bottom: 20px;
                text-decoration: underline;
                &:hover {
                  text-decoration: underline;
                }
                ${text2Medium}
                font-size: ${theme.fontSize.medium};
              `}
              />
              <PopUpVideoLink
                message="How To See Invoices Balance:"
                videoUrl="https://vimeo.com/475865661/e2ec9efe83"
                extraCSS={`
                margin-bottom: 20px;
                text-decoration: underline;
                &:hover {
                  text-decoration: underline;
                }
                ${text2Medium}
                font-size: ${theme.fontSize.medium};
              `}
              />
            </div>
          )}
          <SettingsFormHeader
            id="finance.previousBalanceReloads"
            defaultMessage="Previous Balance Reloads"
          />
          <Table
            key="reloads"
            rowKey="id"
            pagination={false}
            columns={this.columns}
            dataSource={localizedDeposits}
          />
          <div key="reloads_show_all" style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {depositsPagination.total > localizedDeposits.length && (
              <Button type="normal" clickHandler={this.loadMore}>
                Show more
              </Button>
            )}
          </div>
          {!isBifmUser && (
            <>
              <SettingsFormHeader id="finance.invoices" defaultMessage="Invoices" />
              <Table
                key="invoices"
                rowKey="id"
                pagination={false}
                columns={this.columns_invoices}
                dataSource={seeAllInvoices ? invoices : invoices.slice(0, 3)}
              />
              <div key="invoices_show_all" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="normal"
                  clickHandler={() => this.setState({ seeAllInvoices: !seeAllInvoices })}
                >
                  {seeAllInvoices ? 'Show less' : 'Show all'}
                </Button>
              </div>
            </>
          )}
        </Form>
        {loading && <Spinner />}
      </React.Fragment>
    );
  }
}

Balance.propTypes = {
  settingsStore: PropTypes.object,
  sessionStore: PropTypes.object,
  intl: PropTypes.object,
  updateBreadcrumbs: PropTypes.func,
  updateApplyBtn: PropTypes.func,
  autoReloadClone: PropTypes.object,
  // form: PropTypes.object
};

export default Balance;
